// The account-page tile's numbers (docs/lead-relay.md §10.3, §20). Pure, clock
// injected, LOCAL calendar — "this week" and "this month" are the viewer's.
//
// Counts LIVE leads only (the server sends no test leads). A duplicate record
// is a Meta / Zapier re-fire, not a second lead, so it is never counted.
//
// ⚠️⚠️ ABSENCE IS NOT A VALUE. A store with no route has measured nothing, and
// "0 leads" beside it reads as "the ads aren't working". So every row carries a
// STATE, and only a live route's zero is a real zero: a store with no route is
// "not set up", an inactive route is "inactive", a test-mode route is
// "test mode". The counts still show where a route exists (an inactive route
// can hold leads from before it was switched off); for a store with no route
// there is nothing to count and no count is shown.

import type { LeadProvider, LeadSubmissionStatus } from '../../types/leadRelay.js';
import { systemName } from './providerVocabulary.js';

/** Monday 00:00 local of the week containing `now`. */
export function startOfWeek(now: Date): Date {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysSinceMonday = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - daysSinceMonday);
  return d;
}

export function startOfMonth(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

/** The earliest instant the tile needs — the week can start last month. */
export function summarySince(now: Date): Date {
  const week = startOfWeek(now);
  const month = startOfMonth(now);
  return week < month ? week : month;
}

/** What a row's numbers mean. Only `live` makes a zero a measured zero. */
export type StoreState = 'live' | 'test_mode' | 'inactive' | 'not_set_up';

/** What a row's state means, naming the route's own provider (null for a store with no route). */
export function storeStateLabel(state: StoreState, provider: LeadProvider | null): string {
  switch (state) {
    case 'live':
      return 'Live';
    case 'test_mode':
      return `Test mode — leads are not sent to ${systemName(provider)}`;
    case 'inactive':
      return 'Route inactive — new leads land unassigned in the Lead Log';
    case 'not_set_up':
      return 'Not set up — no lead route for this store';
  }
}

export interface StoreSummary {
  /** The route id, or `account:<id>` for a store with no route. */
  key: string;
  routeId: string | null;
  /** The route's provider; null for a store with no route. */
  provider: LeadProvider | null;
  name: string;
  state: StoreState;
  /** null for a store with no route — nothing was measured, so nothing is shown. */
  thisWeek: number | null;
  thisMonth: number | null;
  /** Failed leads received this month — the red number. */
  failedThisMonth: number | null;
  /** Failed leads a person has since keyed in by hand — no longer red (§20). */
  keyedThisMonth: number | null;
}

export function routeState(route: { active: boolean; testMode: boolean }): StoreState {
  if (!route.active) return 'inactive';
  return route.testMode ? 'test_mode' : 'live';
}

export function summarizeLeads(
  routes: ReadonlyArray<{ id: string; name: string; active: boolean; testMode: boolean; provider: LeadProvider }>,
  leads: ReadonlyArray<{ routeId: string; status: LeadSubmissionStatus; receivedAt: string }>,
  now: Date,
  unroutedStores: ReadonlyArray<{ accountId: string; name: string }> = [],
): StoreSummary[] {
  const week = startOfWeek(now).getTime();
  const month = startOfMonth(now).getTime();
  const byRoute = new Map<string, StoreSummary>(
    routes.map((r) => [
      r.id,
      {
        key: r.id,
        routeId: r.id,
        provider: r.provider,
        name: r.name,
        state: routeState(r),
        thisWeek: 0,
        thisMonth: 0,
        failedThisMonth: 0,
        keyedThisMonth: 0,
      },
    ]),
  );
  for (const lead of leads) {
    if (lead.status === 'duplicate') continue;
    const row = byRoute.get(lead.routeId);
    if (!row) continue;
    const at = Date.parse(lead.receivedAt);
    if (!Number.isFinite(at)) continue;
    if (at >= week) row.thisWeek = (row.thisWeek ?? 0) + 1;
    if (at >= month) {
      row.thisMonth = (row.thisMonth ?? 0) + 1;
      if (lead.status === 'failed') row.failedThisMonth = (row.failedThisMonth ?? 0) + 1;
      if (lead.status === 'keyed_manually') row.keyedThisMonth = (row.keyedThisMonth ?? 0) + 1;
    }
  }
  const routed = [...byRoute.values()].sort((a, b) => a.name.localeCompare(b.name));
  const unrouted: StoreSummary[] = [...unroutedStores]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((s) => ({
      key: `account:${s.accountId}`,
      routeId: null,
      provider: null,
      name: s.name,
      state: 'not_set_up',
      thisWeek: null,
      thisMonth: null,
      failedThisMonth: null,
      keyedThisMonth: null,
    }));
  // Stores with a route first; the unconfigured ones after, so they read as a
  // list of gaps rather than interleaving with real figures.
  return [...routed, ...unrouted];
}
